(ns manage.ad-variant-preview.data
  "Check manage.ad-variant.data for functions that are shared
  for other views besides the preview"
  (:require [clojure.string :as str]
            [lemonpi.ads-schema.interface :as ads-schema]
            [manage.ad-variant.data :as variant-data]))

(defn component->resolution-str [x]
  (str (:width x) "x" (:height x)))

(defn get-card-dimensions
  "Calculate preview card dimensions for a variant"
  [{:keys [ad-type component template-dimensions item-scale]}]
  (let [;; default values if the component wasn't found or is rendering
        meta? (variant-data/meta? ad-type)
        tiktok? (variant-data/tiktok? ad-type)
        video? (= :video (variant-data/get-component-type ad-type))
        default-dimensions (cond
                             meta? {:width 1080 :height 1080}
                             tiktok? {:width 1080 :height 1920}
                             video? {:width 1920 :height 1080}
                             (= ad-type "display-html") (select-keys (:value component) [:width :height])
                             :else {:width 400 :height 400})

        {:keys [width height]} (merge default-dimensions
                                      template-dimensions
                                      (:metadata component))

        ;; For now, Meta ads are displayed as 680px wide, because that seems to be the
        ;; maximum width of Facebook posts on a desktop view.
        [width height] (cond
                         meta? [680 (* height (/ 680 width))]
                         :else [width height])

        ;; In non-canvas view, the card size is scaled according to selected zoom level
        scaled-width (if item-scale (* item-scale width) width)
        scaled-height (if item-scale (* item-scale height) height)]
    [scaled-width scaled-height]))

(defn carousel-card-height
  "The height a carousel card should render at for the given card-width, preserving
   its own media's aspect ratio (Meta carousel cards can be 1:1 or 9:16, and even
   vary card by card). Falls back to a 1:1 square if the media has no dimensions."
  [card-width {:keys [width height]}]
  (if (and (pos-int? width) (pos-int? height))
    (* card-width (/ height width))
    card-width))

(def preview-formats ["1:1" "4:5" "9:16" "16:9"])

(def preview-format->ratio
  {"1:1" 1
   "4:5" 0.8
   "9:16" (/ 9 16)
   "16:9" (/ 16 9)})

(defn- media-aspect-ratio [{:keys [width height]}]
  (when (and (pos-int? width) (pos-int? height))
    (/ width height)))

;; Same tolerance content-build-rest-api's facebook/aspect-ratio-equals uses to decide
;; whether a media's aspect ratio is close enough to serve a given Meta placement.
(def aspect-ratio-tolerance 0.05)

(defn aspect-ratio-equals [a b]
  (<= (js/Math.abs (- a b)) aspect-ratio-tolerance))

(defn closest-media-for-ratio
  "Of a card's media renditions, the one matching the target aspect ratio within the
   backend's tolerance. A single-media card just keeps its one media regardless of the
   target. A customized card (several media) returns nil when none match, so the
   preview can show that no asset is available instead of picking a mismatched one."
  [medias target-ratio]
  (if (= 1 (count medias))
    (first medias)
    (some (fn [media]
            (when (some-> (media-aspect-ratio media) (aspect-ratio-equals target-ratio))
              media))
          medias)))

(defn- customized-card?
  "Whether a card has more than one media, i.e. it's customized per Meta placement
   rather than showing the same media regardless of aspect ratio."
  [card]
  (< 1 (count (ads-schema/carousel-card-media-list card))))

(defn unavailable-preview-formats
  "Which preview formats no customized card has an asset for, so the manual upload
   preview can grey those options out. A format stays enabled if any customized
   card covers it; cards that aren't customized (one media, or none yet) don't
   count either way, they always show something regardless of format."
  [cards]
  (let [customized-cards (filter customized-card? cards)]
    (into #{}
          (remove (fn [fmt]
                    (or (empty? customized-cards)
                        (some #(closest-media-for-ratio (ads-schema/carousel-card-media-list %)
                                                        (preview-format->ratio fmt))
                              customized-cards)))
                  preview-formats))))

(defn carousel-multi-placement?
  "Whether any card is customized per Meta placement, making the preview format
   selector worth showing."
  [cards]
  (boolean (some customized-card? cards)))

(defn any-multi-placement-carousel?
  "Whether any of the given variants is a carousel customized per Meta placement,
   i.e. whether the page-level preview format selector applies to anything on screen."
  [variants]
  (boolean (some (fn [{:keys [ad-type components]}]
                   (when (= "meta-carousel" ad-type)
                     (carousel-multi-placement?
                      (:value (variant-data/get-main-component components ad-type)))))
                 variants)))

(def status-filters
  [{:type :status
    :value "draft"
    :visible-for #{:non-reviewer}}
   {:type :status
    :value "approved"
    :visible-for #{:non-reviewer :reviewer}}
   {:type :status
    :value "internal-review"
    :visible-for #{:non-reviewer :reviewer}}
   {:type :status
    :value "rejected"
    :visible-for #{:non-reviewer}}
   {:type :status
    :value "archived"
    :visible-for #{:non-reviewer}}
   {:type :status
    :value "processing"
    :visible-for #{:non-reviewer :reviewer}}
   {:type :status
    :value "error"
    :visible-for #{:non-reviewer :reviewer}}])

(def filters
  {:status {:type "Status"
            :values (merge (into {} (map (juxt key (comp :title val)) variant-data/status))
                           {"processing" "Processing"
                            "error" "Error"})}
   :ad-type {:type "Ad format"
             :values (into {} (map (juxt key (comp :title val)) variant-data/ad-types))}
   :resolution {:type "Resolution"
                :values component->resolution-str}})

(defn get-filter-value-title [k v]
  (let [t (:values (get filters k))]
    (or (t v)
        v)))

(defn get-filter-type-title [type]
  (get-in filters [type :type]))

(def empty-filters {:status #{"draft" "approved" "rejected" "internal-review" "processing" "error"}
                    :ad-type #{}
                    :resolution #{}})

;; Reviewer role cannot see the "rejected" filter toggle, so exclude it from their default.
(def empty-filters-reviewer
  (update empty-filters :status disj "rejected"))

(defn get-filtered-variants
  [variants filters]
  (let [xf identity
        {:keys [search status ad-type resolution]} filters
        search (str/lower-case search)
        xf (if (seq status)
             (comp xf (filter (fn [{:keys [ad-variant-status]}]
                                (or (contains? status ad-variant-status)
                                    (some (fn [[group members]]
                                            (and (contains? status group)
                                                 (contains? members ad-variant-status)))
                                          variant-data/status-filter-groups)))))
             xf)
        xf (if (seq ad-type)
             (comp xf (filter (fn [x]
                                (contains? ad-type (:ad-type x)))))
             xf)
        xf (if (seq resolution)
             (comp xf (filter (fn [x]
                                (contains? resolution (:dimensions x)))))
             xf)
        xf (if (seq search)
             (comp xf (filter (fn [{:keys [content-id]}]
                                (str/includes? (str/lower-case content-id) search))))
             xf)]
    (into [] xf variants)))

(defn get-selected-variants
  "Filter variants first with get-filtered-variants."
  [variants selected-variants]
  (->> variants
       (filterv (comp (or selected-variants #{}) :uuid))))

(defn- add-label [variant]
  (assoc variant ::label (or (when (= "display-html" (:ad-type variant))
                               (when-let [dimensions (:dimensions variant)]
                                 (component->resolution-str dimensions)))
                             (variant-data/ad-type-title (:ad-type variant)))))

(defn list-variants
  "List the variants for sidebar.

  Filter the variants first with get-filtered-variants and optionally
  get-selected-variants."
  [variants]
  (let [variants (->> variants
                      ;; Use label for sorting the variants,
                      ;; this means smaller width and then height is shown first in
                      ;; the list and preview area.
                      (map add-label)
                      (sort-by (juxt :content-id ::label)))]
    (->> (map (fn [variant index prev next]
                (assoc variant
                       ::i (inc index)
                       ::next (:uuid next)
                       ::prev (:uuid prev)))
              variants
              (range)
              (concat [nil] variants)
              (concat (rest variants) [nil]))
         (into []))))

(defn group-variants
  "Groups the variants for either the canvas or list view.

  Filter the variants first with get-filtered-variants and optionally
  get-selected-variants."
  [variants mode]
  (->> variants
       (map add-label)
       (sort-by ::label)
       ;; Grouped items will appear in the order they were in the original collection
       (group-by (if (= "canvas" mode)
                   :content-id
                   :ad-type))
       (sort-by key)
       (into [])))

(defn find-longest-video [video-status]
  (reduce (fn [{:keys [max-duration] :as acc} [_uuid {:keys [duration] :as video}]]
            (if (or (nil? video)
                    (> duration max-duration))
              (assoc acc
                     :video video
                     :max-duration duration)
              acc))
          ;; Use 1 as value if no video variants are selected, for timeline components.
          {:max-duration 1
           :video nil}
          video-status))
